import ColorParser from '@af-common/Common/Utils/ColorParser';
import Library from "@af-common/Resource/Library";
import ModuleImageStore from "../Module/ModuleImage/_stores/ModuleImageStore";
import ModulePatternDesignServiceStore from "../Module/ModulePatternDesignService/_stores/ModulePatternDesignServiceStore";
import { ModuleAction } from "../Module/_actions/ModuleActions";
import { ModuleTypes } from "../Module/_config/ModuleInfo";
import ModuleStore from "../Module/_stores/ModuleStore";
import ImageShader from "../Webgl/Shader/ImageShader";
import SimpleImageShader from "../Webgl/Shader/SimpleImageShader";
import Shader from "../Webgl/Shader/Shader";
import ColorReplaceShader from "../Webgl/Shader/ColorReplaceShader";
import * as Uniform from "../Webgl/Uniform";
import Webgl from "../Webgl/Webgl";
import { IHandler } from "./ModuleHandler";
import { IS_SERVER } from '@af-app/config';

type ChunkingData = {
	x: number,
	y: number,
	original_width: number,
	original_height: number,
	offset_x: number,
	offset_y: number,
}

export default class ImageHandler implements IHandler {
	private renderImage = (source: any, webgl: Webgl, store: ModuleImageStore, inputSizeWidth: number, inputSizeHeight: number, documentWidth: number, documentHeight: number, chunking_data: ChunkingData = null) => {
		webgl = webgl || Webgl.getInstance("worker");

		let shader: Shader;
		let programId = store.get('id')

		let width = store.get('width');
		let height = store.get('height');
		let x = store.get('x');
		let y = store.get('y');
		let rot = store.get("rotation") / (180/Math.PI)

		if(chunking_data) {
			programId += "_chunks";
			if(!webgl.programs[programId]){
				webgl.createProgram(programId, new SimpleImageShader());
			}
			shader = webgl.programs[programId].shader as SimpleImageShader;
			webgl.useProgram(programId);

			let rxi = width / chunking_data.original_width;
			let ryi = height / chunking_data.original_height;

			let rx = source.content.width / chunking_data.original_width;
			let ry = source.content.height / chunking_data.original_height;

			let hw = width/2
			let hh = height/2

			let temp_w = width * rx;
			let temp_h = height * ry;
			let temp_x = x - hw + ((rxi * 4096) * chunking_data.x) + temp_w/2
			let temp_y = y - hh + ((ryi * 4096) * chunking_data.y) + temp_h/2

			let final_x = Math.cos(rot) * (temp_x - x) - Math.sin(rot) * (temp_y - y) + x;
			let final_y = Math.sin(rot) * (temp_x - x) + Math.cos(rot) * (temp_y - y) + y;

			width = temp_w+1;
			height = temp_h+1;

			shader.u_offset = new Uniform.Uniform2f(chunking_data.offset_x, chunking_data.offset_y);
			shader.u_origin = new Uniform.Uniform2f(x, y);

			x = final_x - x;
			y = final_y - y;
		} else {
			if(!webgl.programs[programId]){
				webgl.createProgram(programId, new ImageShader());
			}
			shader = webgl.programs[programId].shader as ImageShader;
			webgl.useProgram(programId);
		}

		shader.u_documentSize = new Uniform.Uniform2f(documentWidth, documentHeight);
		shader.u_inputSize = new Uniform.Uniform2f(inputSizeWidth, inputSizeHeight);

		shader.u_translation = new Uniform.Uniform2f(x, y);
		shader.u_size = new Uniform.Uniform2f(width, height);
		shader.u_rotation = new Uniform.Uniform1f(rot);

		shader.u_premul = new Uniform.Uniform1i(IS_SERVER ? 0.0 : 1.0);

		shader.u_imageSize = new Uniform.Uniform2f(source.content.width, source.content.height);
		webgl.currentProgram.setTexture("u_image", source.content);

		webgl.render();
	}

	public getOutput(module: ModuleStore, webgl: Webgl, documentWidth:number, documentHeight:number, quality:number, useChunks:boolean, updater: (key:string, value:any) => ModuleAction) {
		let store:any = undefined;

		let fbo = webgl.fbos[webgl.currentFramebuffer];
		let inputSizeWidth = fbo.width * (100 / quality);
		let inputSizeHeight = fbo.height * (100 / quality);

		let source: any;
		switch(module.get('type')) {
			case ModuleTypes.Image:
				let UserImages = Library.UserImageLibrary;
				store = module as ModuleImageStore;

				let id = store.get("source");
				let width = store.get("width");
				let height = store.get("height");

				if(!(id&&width&&height)) return;

				source = UserImages.get(id);

				if(!source || !source.value) {
					updater("source", null);
					break;
				}

				if (useChunks) {
					let cx = Math.ceil(source.extra.width / 4096);
					let cy = Math.ceil(source.extra.height / 4096);
					
					for(let offset_y = -1; offset_y <= 1; offset_y++){
						for(let offset_x = -1; offset_x <= 1; offset_x++){
							for (let y = 0; y < cy; y++) {
								for (let x = 0; x < cx; x++) {
									let chunk = UserImages.getChunk(id, x, y);
									this.renderImage(chunk.value, null, store, inputSizeWidth, inputSizeHeight, documentWidth, documentHeight, {x: x, y: y, original_width: source.extra.width, original_height: source.extra.height, offset_x: offset_x, offset_y: offset_y});
								}
							}
						}
					}
				} else {
					this.renderImage(source.value, webgl, store, inputSizeWidth, inputSizeHeight, documentWidth, documentHeight);
				}
				break;

			case ModuleTypes.PatternDesignService:
				let PatternImages = Library.PatternDesignServiceLibrary;
				store = module as ModulePatternDesignServiceStore;

				id = store.get("source");

				if(!id) return;

				source = PatternImages.get(id);

				if(!source || !source.value) {
					updater("source", null);
					break;
				}

				source = source.value;

				if(!source.content) {
					updater("source", null);
					break;
				}

				//Image pass
				if(!webgl.programs[store.get("id")]){
					webgl.createProgram(store.get("id"), new ImageShader());
				}

				let shader = webgl.programs[store.get("id")].shader as ImageShader;

				webgl.useProgram(store.get('id'));

				shader.u_imageSize = new Uniform.Uniform2f(source.content.width, source.content.height);
				shader.u_documentSize = new Uniform.Uniform2f(documentWidth, documentHeight);
				shader.u_inputSize = new Uniform.Uniform2f(inputSizeWidth, inputSizeHeight);
				shader.u_translation = new Uniform.Uniform2f(inputSizeWidth / 2, inputSizeHeight / 2);
				shader.u_size = new Uniform.Uniform2f(inputSizeWidth, inputSizeHeight);
				shader.u_rotation = new Uniform.Uniform1f(0);
				shader.u_premul = new Uniform.Uniform1i(IS_SERVER ? 0.0 : 1.0);

				webgl.currentProgram.setTexture("u_image", source.content);

				webgl.render();

				webgl = Webgl.getInstance("worker");
				fbo = webgl.fbos[webgl.currentFramebuffer];

				//Color Replace pass
				let colorReplaceId = store.get("id")+'_color_replace';
				if(!webgl.programs[colorReplaceId]){
					webgl.createProgram(colorReplaceId, new ColorReplaceShader());
				}

				let colorReplaceShader = webgl.programs[colorReplaceId].shader as ColorReplaceShader;
				webgl.useProgram(colorReplaceId);

				let colors = store.get('colors').toArray();
				let color_replacements = store.get('color_replacements').toArray();
				for (let i = 0; i < 6; i++) {
					if(colors[i]) {
						colorReplaceShader['u_color'][i] = ColorParser.stringToUniform(colors[i]);
						colorReplaceShader['u_color_replacement'][i] = ColorParser.stringToUniform(color_replacements[i]);
					} else {
						colorReplaceShader['u_color'][i] = [0.0, 0.0, 0.0, 0.0];
						colorReplaceShader['u_color_replacement'][i] = [0.0, 0.0, 0.0, 0.0];
					}
				}

				webgl.render();
				break;
		}
	}
}